import { render, h } from 'preact';
import { useEffect, useRef, useContext } from 'preact/hooks';
import { batch } from '@preact/signals';

import { parseConfig }   from './services/configParser.js';
import { SessionManager } from './services/session.js';
import { ColorEngine }   from './services/colorEngine.js';
import { ApiClient }     from './services/api.js';
import { AnalyticsQueue, AnalyticsClient } from './services/analytics.js';
import { EventBus }      from './utils/eventBus.js';
import { MC }            from './utils/constants.js';
import { MC_STYLES, FLOATPANEL_STYLES } from './styles/widget.css.js';
import { ChatContext }   from './hooks/context.js';
import { useTheme }      from './hooks/useTheme.js';
import { useAnalytics }  from './hooks/useAnalytics.js';
import { useSession }    from './hooks/useSession.js';
import { PopupLayout }   from './layouts/PopupLayout.jsx';
import { PanelLayout }   from './layouts/PanelLayout.jsx';
import { ErrorBoundary } from './components/ErrorBoundary.jsx';
import {
  isOpen, isMobile, isCompact, isMinimized, suggestions,
  setOpen, showReview, unreadCount,
} from './store/chatStore.js';

// ── Widget token store (module-level singleton) ──────────────────────────────
window.__mcWidgetTokenStore = window.__mcWidgetTokenStore || { token: null, expiresAt: 0 };

async function acquireWidgetToken(cfg) {
  try {
    const res = await fetch(
      `${cfg.apiUrl}/widget-auth/${encodeURIComponent(cfg.appId)}/token`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } }
    );
    if (!res.ok) { console.warn('[MC] Widget token request failed:', res.status); return; }
    const data = await res.json();
    window.__mcWidgetTokenStore.token     = data.token || null;
    window.__mcWidgetTokenStore.expiresAt = Date.now() + ((data.expires_in || 86400) - 300) * 1000;
  } catch (e) { console.warn('[MC] Widget token fetch error:', e); }
}

function getWidgetToken() {
  const s = window.__mcWidgetTokenStore;
  return (!s.token || Date.now() >= s.expiresAt) ? null : s.token;
}

async function resolveUserIdentity(cfg, session, sessionData) {
  const email = cfg.userEmail;
  if (!email) return;
  const cached = session.getUserLookup(email);
  if (cached !== null) {
    if (cached.external_user_id && cached.external_user_id !== sessionData.userId) {
      Object.assign(sessionData, session.patchUserId(cached.external_user_id));
    }
    return;
  }
  try {
    const res = await fetch(`${cfg.apiUrl}/chat/user/lookup`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app_id: cfg.appId, email }),
    });
    if (!res.ok) return;
    const data = await res.json();
    session.setUserLookup(email, data.found ? data.external_user_id : null);
    if (data.found && data.external_user_id && data.external_user_id !== sessionData.userId) {
      Object.assign(sessionData, session.patchUserId(data.external_user_id));
    }
  } catch {}
}

// ── Activity observer ────────────────────────────────────────────────────────
class ActivityObserver {
  constructor(onIdle, idleSeconds = 30) {
    this._onIdle  = onIdle;
    this._idleMs  = idleSeconds * 1000;
    this._timer   = null;
    this._active  = false;
    this._fired   = false;
    this._handler = this._reset.bind(this);
    this._events  = ['mousemove','click','keydown','scroll','touchstart'];
  }
  start() {
    if (this._active) return;
    this._active = true;
    this._events.forEach(e => document.addEventListener(e, this._handler, { passive: true }));
    this._reset();
  }
  stop() {
    clearTimeout(this._timer);
    this._active = false;
    this._events.forEach(e => document.removeEventListener(e, this._handler));
  }
  resetForNewSession() { this._fired = false; this._reset(); }
  _reset() {
    clearTimeout(this._timer);
    if (this._fired) return;
    this._timer = setTimeout(() => { this._fired = true; this._onIdle(); }, this._idleMs);
  }
}

// ── Plugin loader ────────────────────────────────────────────────────────────
class PluginLoader {
  constructor(bus) { this._bus = bus; this._loaded = new Set(); }
  async load(url, publicAPI) {
    if (!url || this._loaded.has(url)) return;
    try {
      const mod = await import(/* webpackIgnore:true */ /* @vite-ignore */ url);
      if (typeof mod.default !== 'function') return;
      await mod.default(publicAPI);
      this._loaded.add(url);
      this._bus.emit('plugin:load', { url, id: url.split('/').pop().replace(/\.[^.]+$/, '') });
    } catch (e) { console.error('[MC] Plugin failed:', url, e); }
  }
}

// ── Inner app — runs inside ChatContext.Provider ─────────────────────────────
function ChatAppInner({ hostEl, activityObserver, plugins, setColor }) {
  const { cfg, session, bus, api, analytics } = useContext(ChatContext);
  useAnalytics();
  const { loadHistory, makeWelcomeMsgs } = useSession();

  // Bootstrap on mount
  useEffect(() => {
    const applyResponsive = () => {
      const w        = window.innerWidth;
      const h        = window.innerHeight;
      const isNarrow = w < cfg.mobileBreakpoint;
      const isShort  = h < 700;
      const mob      = isNarrow && cfg.mobileFullscreen;
      const cmp      = !mob && isShort;
      batch(() => {
        isMobile.value  = mob;
        isCompact.value = cmp;
      });
      if (hostEl) {
        hostEl.toggleAttribute('data-mobile',  mob);
        hostEl.toggleAttribute('data-compact', cmp);
      }
      bus.emit('resize', { mobile: mob, compact: cmp, width: w, height: h });
    };
    const ro = new ResizeObserver(applyResponsive);
    ro.observe(document.documentElement);
    applyResponsive();

    const onKeydown = (e) => {
      if (e.key !== 'Escape' || !isOpen.value) return;
      import('./store/chatStore.js').then(({ showHistory }) => {
        if (showHistory.value) { showHistory.value = false; }
        else { setOpen(false); bus.emit('close', { sessionId: session.getOrCreate().sessionId }); }
      });
    };
    document.addEventListener('keydown', onKeydown);

    const initMessages = async () => {
      if (cfg.persistHistory) {
        await loadHistory();
        import('./store/chatStore.js').then(({ messages, addMessage }) => {
          if (messages.value.length === 0) {
            makeWelcomeMsgs().forEach(m => addMessage(m));
          }
        });
      } else {
        makeWelcomeMsgs().forEach(m => {
          import('./store/chatStore.js').then(({ addMessage }) => addMessage(m));
        });
      }
      if (cfg.suggestedQuestions?.length) {
        suggestions.value = cfg.suggestedQuestions;
      }
    };
    initMessages();

    const prefs = session.getPrefs();
    if (prefs.minimized && hostEl) hostEl.toggleAttribute('data-minimized', true);

    if (cfg.autoOpen) {
      setTimeout(() => {
        setOpen(true);
        if (hostEl) hostEl.toggleAttribute('data-open', true);
        bus.emit('open', { sessionId: session.getOrCreate().sessionId });
      }, cfg.openDelay || 0);
    }

    const buildPublicAPI = () => ({
      open:             () => { setOpen(true);  bus.emit('open',  { sessionId: session.getOrCreate().sessionId }); },
      close:            () => { setOpen(false); bus.emit('close', { sessionId: session.getOrCreate().sessionId }); },
      toggle:           () => { isOpen.value ? setOpen(false) : setOpen(true); },
      sendMessage:      (text) => { if (text?.trim()) bus.emit('_send', { text }); },
      addSystemMessage: (text) => {
        import('./store/chatStore.js').then(({ addMessage }) => {
          import('./utils/helpers.js').then(({ msgId }) => {
            addMessage({ id: msgId(), role: 'system', text, timestamp: Date.now() });
          });
        });
      },
      setSuggestions:   (qs) => { suggestions.value = qs || []; },
      setColor:         (prop, val) => setColor(prop, val),
      endSession:       (exitUrl) => analytics?.endSession(exitUrl),
      on:               (ev, fn) => bus.on(ev, fn),
      session:          () => ({ ...session.getOrCreate() }),
      analytics:        { deviceType: analytics?.deviceType, browser: analytics?.browser },
      config:           cfg,
      version:          MC.VERSION,
    });

    if (cfg.subScript) plugins.load(cfg.subScript, buildPublicAPI());
    bus.emit('load', { sessionId: session.getOrCreate().sessionId, version: MC.VERSION });

    return () => {
      ro.disconnect();
      document.removeEventListener('keydown', onKeydown);
      activityObserver.stop();
    };
  }, []);

  // Sync isOpen → host element attribute
  useEffect(() => {
    if (!hostEl) return;
    hostEl.toggleAttribute('data-open', isOpen.value);
  }, [isOpen.value]);

  const isFloatpanel = cfg.variant === 'floatpanel';
  return isFloatpanel
    ? <PanelLayout hostEl={hostEl} />
    : <PopupLayout hostEl={hostEl} />;
}

// ── Root App component — only sets up context + theme ────────────────────────
function ChatApp({ cfg, session, bus, api, analytics, activityObserver, plugins, hostEl }) {
  const activityRef = useRef(activityObserver);
  const { setColor } = useTheme(hostEl, cfg);

  const contextValue = {
    cfg, session, bus, api, analytics,
    activityRef,
    plugins,
  };

  return (
    <ChatContext.Provider value={contextValue}>
      <ErrorBoundary>
        <ChatAppInner
          hostEl={hostEl}
          activityObserver={activityObserver}
          plugins={plugins}
          setColor={setColor}
        />
      </ErrorBoundary>
    </ChatContext.Provider>
  );
}

// ── Custom Element ───────────────────────────────────────────────────────────
class MagecompChatElement extends HTMLElement {
  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: 'closed' });
  }

  connectedCallback() {
    // Services passed in via Object.assign before define
    const { _cfg: cfg, _session: session, _bus: bus, _api: api, _analytics: analytics, _activityObserver: actObserver, _plugins: plugins } = this;
    const isFloatpanel = cfg.variant === 'floatpanel';
    const styles = isFloatpanel ? `${MC_STYLES}${FLOATPANEL_STYLES}` : MC_STYLES;

    // Inject style
    const style = document.createElement('style');
    style.textContent = styles;
    this._shadow.appendChild(style);

    // Host attributes
    if (isFloatpanel) this.setAttribute('data-variant', 'floatpanel');
    this.setAttribute('data-position', cfg.position);
    if (cfg.rtl) this.setAttribute('dir', 'rtl');

    // Mount Preact into shadow root
    render(
      h(ChatApp, { cfg, session, bus, api, analytics, activityObserver: actObserver, plugins, hostEl: this }),
      this._shadow
    );
  }

  disconnectedCallback() {
    render(null, this._shadow);
  }
}

// ── Bootstrap ────────────────────────────────────────────────────────────────
(async function () {
  if (customElements.get(MC.TAG)) return;

  let cfg;
  try { cfg = parseConfig(); }
  catch (e) { console.error('[MC]', e); return; }

  if (!cfg.appId)  { console.error('[MC] data-app-id is required');  return; }
  if (!cfg.apiUrl) { console.error('[MC] data-api-url is required'); return; }

  const session     = new SessionManager(cfg);
  const sessionData = session.getOrCreate();

  await acquireWidgetToken(cfg);
  await resolveUserIdentity(cfg, session, sessionData);

  const bus         = new EventBus(cfg.appId);
  const aQueue      = new AnalyticsQueue();
  const analytics   = new AnalyticsClient(cfg, sessionData, aQueue);
  const api         = new ApiClient(cfg, sessionData, bus, analytics, getWidgetToken);
  const plugins     = new PluginLoader(bus);
  const actObserver = new ActivityObserver(
    () => { import('./store/chatStore.js').then(({ showReview, messages }) => {
      if (messages.value.filter(m => m.role === 'user').length > 0) showReview.value = true;
    }); },
    parseInt(cfg.inactivityReviewSeconds || 30, 10)
  );

  // Inject services before define
  Object.assign(MagecompChatElement.prototype, {
    _cfg:               cfg,
    _session:           session,
    _bus:               bus,
    _api:               api,
    _analytics:         analytics,
    _plugins:           plugins,
    _activityObserver:  actObserver,
  });

  customElements.define(MC.TAG, MagecompChatElement);

  const el = document.createElement(MC.TAG);
  el.setAttribute('data-app-id', cfg.appId);

  const inject = () => document.body.appendChild(el);
  if (document.body) { inject(); }
  else { document.addEventListener('DOMContentLoaded', inject, { once: true }); }

  // Public window API
  window.MagecompChat = {
    open:             () => { setOpen(true); bus.emit('open', { sessionId: sessionData.sessionId }); },
    close:            () => { setOpen(false); bus.emit('close', { sessionId: sessionData.sessionId }); },
    toggle:           () => { isOpen.value ? setOpen(false) : setOpen(true); },
    sendMessage:      (text) => { bus.emit('_send', { text }); },
    addSystemMessage: (text) => {
      import('./store/chatStore.js').then(({ addMessage }) => {
        import('./utils/helpers.js').then(({ msgId }) => {
          addMessage({ id: msgId(), role: 'system', text, timestamp: Date.now() });
        });
      });
    },
    setSuggestions:   (qs) => { suggestions.value = qs || []; },
    setColor:         (p, v) => { new ColorEngine(cfg).setColor(p, v, el); },
    endSession:       (exitUrl) => analytics.endSession(exitUrl),
    on:               (ev, fn) => bus.on(ev, fn),
    session:          () => ({ ...session.getOrCreate() }),
    analytics:        { deviceType: analytics.deviceType, browser: analytics.browser },
    config:           cfg,
    version:          MC.VERSION,
  };

  console.info(`[MageComp Chat] v${MC.VERSION} | app:${cfg.appId} | session:${sessionData.sessionId}`);
})();
